import React, { useState, useEffect } from 'react';
import EditableSection from './EditableSection';
import { Plus, Trash2 } from 'lucide-react';

const PersonalInfoEditor = ({ data, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(data || {
    name: '', role: '', email: '', phone: '', location: '', links: []
  });

  useEffect(() => {
    if (data) setEditData(data);
  }, [data]);

  const handleSave = () => {
    onSave(editData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditData(data || { name: '', role: '', email: '', phone: '', location: '', links: [] });
    setIsEditing(false);
  };

  const addLink = () => {
    setEditData({
      ...editData,
      links: [...(editData.links || []), { label: '', url: '' }]
    });
  };

  const updateLink = (index, field, value) => {
    const newLinks = [...(editData.links || [])];
    newLinks[index] = { ...newLinks[index], [field]: value };
    setEditData({ ...editData, links: newLinks });
  };

  const removeLink = (index) => {
    const newLinks = (editData.links || []).filter((_, i) => i !== index);
    setEditData({ ...editData, links: newLinks });
  };

  return (
    <EditableSection 
      isEditing={isEditing} 
      onEdit={() => setIsEditing(true)} 
      onCancel={handleCancel} 
      onSave={handleSave}
    >
      {isEditing ? (
        <div className="bg-gray-50 p-4 rounded-md border border-gray-200 font-sans space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
              <input 
                type="text" 
                value={editData.name || ''} 
                onChange={(e) => setEditData({...editData, name: e.target.value})}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none"
                placeholder="John Doe"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <input 
                type="text" 
                value={editData.role || ''} 
                onChange={(e) => setEditData({...editData, role: e.target.value})}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none"
                placeholder="Software Engineer"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <input 
                type="text" 
                value={editData.location || ''} 
                onChange={(e) => setEditData({...editData, location: e.target.value})}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none"
                placeholder="City, Country"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input 
                type="email" 
                value={editData.email || ''} 
                onChange={(e) => setEditData({...editData, email: e.target.value})}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none"
                placeholder="john@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
              <input 
                type="tel" 
                value={editData.phone || ''} 
                onChange={(e) => setEditData({...editData, phone: e.target.value})}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none"
                placeholder="+91 xxxxxxxxxx"
              />
            </div>
          </div>
          
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">Links</label>
              <button onClick={addLink} className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-xs font-medium">
                <Plus size={14} /> Add Link
              </button>
            </div>
            <div className="space-y-2">
              {(editData.links || []).map((link, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <input 
                    type="text" 
                    value={link.label} 
                    onChange={(e) => updateLink(idx, 'label', e.target.value)}
                    className="w-1/3 border border-gray-300 rounded px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none"
                    placeholder="Label (e.g. GitHub)"
                  />
                  <input 
                    type="text" 
                    value={link.url} 
                    onChange={(e) => updateLink(idx, 'url', e.target.value)}
                    className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none"
                    placeholder="URL (e.g. https://github.com/...)"
                  />
                  <button onClick={() => removeLink(idx)} className="text-red-500 hover:text-red-700 p-2 transition-colors">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              {(!editData.links || editData.links.length === 0) && (
                <div className="text-sm text-gray-500 italic py-2">No links added.</div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center mb-4">
          <h1 className="text-[28px] font-bold font-serif text-gray-900 mb-[2px]">{data?.name || "Your Name"}</h1>
          <div className="text-[17px] font-serif mb-2 text-gray-800">{data?.role || "Your Role"}</div>
          
          <div className="flex flex-wrap items-center justify-center gap-[10px] text-[14.5px] font-serif text-gray-800">
            {data?.location && <span>{data.location}</span>}
            {data?.location && (data?.email || data?.phone) && <span className="text-gray-400 font-light">&mdash;</span>}
            {data?.email && <span>{data.email}</span>}
            {data?.email && data?.phone && <span className="text-gray-400 font-light">&mdash;</span>}
            {!data?.email && data?.location && data?.phone && <span className="text-gray-400 font-light">&mdash;</span>}
            {data?.phone && <span>{data.phone}</span>}
            
            {(data?.links || []).map((link, idx) => (
              <React.Fragment key={idx}>
                {((idx === 0 && (data?.location || data?.email || data?.phone)) || idx > 0) && (
                  <span className="text-gray-400 font-light">&mdash;</span>
                )}
                {link.url ? (
                  <a href={link.url} target="_blank" rel="noreferrer" className="hover:underline">
                    {link.label}
                  </a>
                ) : (
                  <span>{link.label}</span>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </EditableSection>
  );
};

export default PersonalInfoEditor;
